import { 
    type MaybeRefOrGetter,
    type WatchSource,
    isRef,
    onScopeDispose,
    toValue,
    watch
 } from 'vue';

type AnyAsyncFunction = (signal?: AbortSignal) => Promise<unknown>;


export const usePolling = (
    callback: AnyAsyncFunction,
    interval: MaybeRefOrGetter<number> = 4000,
    condition?: WatchSource<boolean>
) => {
    let pollingTimeoutId: ReturnType<typeof setTimeout> | null = null;
    let abortController: AbortController | null = null;

    const isManuallyAborted = () => (
        !pollingTimeoutId ||
        abortController?.signal.aborted ||
        (condition && !toValue(condition))
    );

    const stop = (keepRouterListener?: Event | boolean) => {
        if (pollingTimeoutId) {
            clearTimeout(pollingTimeoutId);
            pollingTimeoutId = null;
        }

        if (abortController) {
            abortController.abort();
            abortController = null;
        }

        if (keepRouterListener === true) {
            document.removeEventListener("onRouterGeneralLeaveHook", stop);
        }
    };

    const start = async (immediate = false) => {
        stop(true);
        abortController = new AbortController();

        document.addEventListener("onRouterGeneralLeaveHook", stop, { passive: true });

        if (immediate) {
            await callback(abortController?.signal);
            return start();
        }

        pollingTimeoutId = setTimeout(async () => {
            // already cancelled through: outer logic
            if (isManuallyAborted()) return;

            try {
                await callback(abortController?.signal);
            }
            catch (error) {
                if (error instanceof Error && error.name === 'AbortError') {
                    return;
                }
            }

            // already cancelled through: callback logic
            if (isManuallyAborted()) return;

            // recycle timeout
            start();

        }, toValue(interval));
    };

    onScopeDispose(() => {
        stop();
    });

    if (condition) {
        watch(
            condition,
            (isEnabled) => {
                if (isEnabled) {
                    start(true);
                    return;
                }
                stop();
            },
        );
    }

    if (isRef(interval)) {
        watch(
            interval,
            () => start()
        );
    }

    return {
        start,
        stop,
    };
};
